import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getEvent } from '../../../features/event/eventSlice';
import { list as listAudit, reportActivity } from '../../../features/audit/auditService';
import { exportAuditXlsx } from '../../../helpers/auditExport';
import AuditTable, { SOURCE_LABEL } from './auditTable';
import AuditDetailsDialog from './auditDetailsDialog';

const ACTOR_OPTIONS = [
  { value: 'user', label: 'BowlScout users' },
  { value: 'portal_personal', label: 'Portal contacts' },
  { value: 'portal_shared', label: 'Shared portal PIN' },
  { value: 'anonymous', label: 'Unknown devices' }
];

const SOURCE_OPTIONS = Object.entries(SOURCE_LABEL).map(([value, label]) => ({
  value,
  label
}));

const portalLabel = (scope) => {
  if (scope?.venueName) return scope.venueName;
  if (scope?.kind === 'secretary') return 'Tournament Secretary';
  if (scope?.kind === 'club') return 'Club portal';
  return 'This event';
};

const dateBoundary = (value, endOfDay = false) => {
  if (!value) return '';
  const date = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00'}`);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const EventAuditPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const event = useSelector((state) => state.event.event);

  const [view, setView] = useState('changes');
  const [items, setItems] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState(null);

  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterActor, setFilterActor] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterOutcome, setFilterOutcome] = useState('');
  const [filterVenue, setFilterVenue] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    dispatch(getEvent(eventId));
  }, [dispatch, eventId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    listAudit(eventId, {
      view,
      from: dateBoundary(fromDate),
      to: dateBoundary(toDate, true)
    })
      .then((response) => {
        if (cancelled) return;
        setItems(response?.items || []);
        setTruncated(Boolean(response?.truncated));
      })
      .catch((requestError) => {
        if (cancelled) return;
        setItems([]);
        setTruncated(false);
        setError(
          requestError?.response?.data?.message ||
            'The audit log could not be loaded. Please try again.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, view, fromDate, toDate, reload]);

  const actionOptions = useMemo(() => {
    const labels = new Map();
    items.forEach((item) => {
      if (item.action && !labels.has(item.action)) {
        labels.set(item.action, item.actionLabel || item.action);
      }
    });
    return [...labels.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const venueOptions = useMemo(() => {
    const names = new Set();
    items.forEach((item) => names.add(portalLabel(item.scope)));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return items.filter((item) => {
      if (filterAction && item.action !== filterAction) return false;
      if (filterActor && item.actor?.type !== filterActor) return false;
      if (filterSource && (item.source || 'organiser') !== filterSource) return false;
      if (view === 'access' && filterOutcome && item.outcome !== filterOutcome) {
        return false;
      }
      if (filterVenue && portalLabel(item.scope) !== filterVenue) return false;

      if (term) {
        const haystack = [
          item.actionLabel,
          item.action,
          item.actor?.name,
          item.actor?.email,
          item.entity?.name,
          item.summary,
          item.source,
          SOURCE_LABEL[item.source],
          item.scope?.venueName,
          item.ip
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      return true;
    });
  }, [
    items,
    search,
    filterAction,
    filterActor,
    filterSource,
    filterOutcome,
    filterVenue,
    view
  ]);

  const changeView = (nextView) => {
    if (nextView === view) return;
    setView(nextView);
    setFilterAction('');
    setFilterSource('');
    setFilterOutcome('');
    setSelected(null);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterAction('');
    setFilterActor('');
    setFilterSource('');
    setFilterOutcome('');
    setFilterVenue('');
    setFromDate('');
    setToDate('');
  };

  const hasFilters = Boolean(
    search ||
      filterAction ||
      filterActor ||
      filterSource ||
      filterOutcome ||
      filterVenue ||
      fromDate ||
      toDate
  );

  const eventName = event?.id === eventId ? event.name : '';
  const isAccess = view === 'access';

  const handleExport = () => {
    if (filtered.length === 0) return;

    exportAuditXlsx({ eventName, view, items: filtered });
    reportActivity(eventId, {
      operation: 'audit.export',
      entityId: eventId,
      entityName: eventName || 'Audit log',
      summary: `${isAccess ? 'Portal access' : 'Changes'} · ${filtered.length} record${
        filtered.length === 1 ? '' : 's'
      } · XLSX`,
      metadata: { view, recordCount: filtered.length, format: 'xlsx' }
    });
  };

  let emptyMessage = 'No activity has been recorded yet.';
  if (isAccess) {
    emptyMessage = 'No portal sign-ins have been recorded in this period.';
  }
  if (hasFilters) {
    emptyMessage = 'No audit records match these filters.';
  }

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Box mb={2}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/events/${encodeURIComponent(eventId)}`)}>
          Back to Event
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>
        {eventName ? `${eventName} — Audit log` : 'Audit log'}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        See who signed in to the scoring portal, scored games, changed this event,
        printed QR codes, or exported logs and draws. Changes are kept indefinitely;
        portal access records (including IP address and browser) are kept for 12
        months.
      </Typography>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={view}
          onChange={(eventObject, nextView) => changeView(nextView)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Audit views">
          <Tab value="changes" label="Changes" />
          <Tab value="access" label="Portal access" />
        </Tabs>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="Search"
            placeholder="Name, email, action, item or IP"
            size="small"
            value={search}
            onChange={(eventObject) => setSearch(eventObject.target.value)}
            sx={{ minWidth: { xs: '100%', md: 240 } }}
          />

          <TextField
            select
            label="Action"
            size="small"
            value={filterAction}
            onChange={(eventObject) => setFilterAction(eventObject.target.value)}
            sx={{ minWidth: { xs: '100%', md: 200 } }}>
            <MenuItem value="">All actions</MenuItem>
            {actionOptions.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Actor"
            size="small"
            value={filterActor}
            onChange={(eventObject) => setFilterActor(eventObject.target.value)}
            sx={{ minWidth: { xs: '100%', md: 180 } }}>
            <MenuItem value="">All actors</MenuItem>
            {ACTOR_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          {!isAccess && (
            <TextField
              select
              label="Source"
              size="small"
              value={filterSource}
              onChange={(eventObject) => setFilterSource(eventObject.target.value)}
              sx={{ minWidth: { xs: '100%', md: 150 } }}>
              <MenuItem value="">All sources</MenuItem>
              {SOURCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isAccess && (
            <TextField
              select
              label="Outcome"
              size="small"
              value={filterOutcome}
              onChange={(eventObject) => setFilterOutcome(eventObject.target.value)}
              sx={{ minWidth: { xs: '100%', md: 150 } }}>
              <MenuItem value="">All outcomes</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failure">Failed</MenuItem>
            </TextField>
          )}

          <TextField
            select
            label="Portal"
            size="small"
            value={filterVenue}
            onChange={(eventObject) => setFilterVenue(eventObject.target.value)}
            sx={{ minWidth: { xs: '100%', md: 190 } }}>
            <MenuItem value="">All portals</MenuItem>
            {venueOptions.map((venue) => (
              <MenuItem key={venue} value={venue}>
                {venue}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="From"
            type="date"
            size="small"
            value={fromDate}
            onChange={(eventObject) => setFromDate(eventObject.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: '100%', md: 160 } }}
          />

          <TextField
            label="To"
            type="date"
            size="small"
            value={toDate}
            onChange={(eventObject) => setToDate(eventObject.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: '100%', md: 160 } }}
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            disabled={!hasFilters}
            sx={{ whiteSpace: 'nowrap' }}>
            Clear
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={filtered.length === 0}
            sx={{ whiteSpace: 'nowrap' }}>
            Export
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => setReload((value) => value + 1)}
            disabled={loading}
            sx={{ whiteSpace: 'nowrap' }}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        {isAccess
          ? 'Portal access records expire after 12 months. Change records are kept indefinitely.'
          : 'Change records are kept indefinitely. Portal access records expire after 12 months.'}
      </Alert>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReload((value) => value + 1)}>
              Retry
            </Button>
          }>
          {error}
        </Alert>
      )}

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only the newest 1,000 records are shown. Narrow the date range to find older
          records.
        </Alert>
      )}

      {loading ? (
        <Paper variant="outlined" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={32} />
        </Paper>
      ) : filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, px: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {error ? 'The audit log is unavailable.' : emptyMessage}
          </Typography>
        </Paper>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Showing {filtered.length} of {items.length} loaded record
            {items.length === 1 ? '' : 's'}.
          </Typography>
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <AuditTable items={filtered} view={view} onSelect={setSelected} />
          </Paper>
        </>
      )}

      <AuditDetailsDialog
        open={Boolean(selected)}
        item={selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
};

export default EventAuditPage;
